import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  WizardFindOption,
  buildWizardFindOptions,
  wizardFindOptionLabel,
} from 'src/helpers/userCheckinWizard';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IUser } from 'src/interfaces/User';

interface Props {
  users: IUser[];
  unownedParticipants: IEnrichedParticipant[];
  value: WizardFindOption | null;
  onChange: (option: WizardFindOption | null) => void;
  disabled?: boolean;
}

export default function WizardFindAutocomplete({
  users,
  unownedParticipants,
  value,
  onChange,
  disabled = false,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const options = useMemo(
    () => buildWizardFindOptions(users, unownedParticipants, query),
    [users, unownedParticipants, query],
  );

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, next) => setQuery(next)}
      getOptionLabel={wizardFindOptionLabel}
      isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
      groupBy={option =>
        option.kind === 'user'
          ? t('userCheckinWizard.findGroupUsers')
          : t('userCheckinWizard.findGroupParticipants')
      }
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={t('userCheckinWizard.findUserLabel')}
          placeholder={t('userCheckinWizard.findUserPlaceholder')}
          margin="none"
          fullWidth
        />
      )}
    />
  );
}
